import { mobileRegex, emailRegex, panRegex } from '../../../../utils/constants';
import * as Yup from 'yup';
import enUs from '../../../../lang/en-us';

const { dashboard } = enUs;

const drivingLicenseRegex = /^[A-Z]{2}[A-Za-z0-9_]{8,48}$/;
const aadharRegex = new RegExp('^[0-9]{4}$');
const passportNumberRegex = /(^[A-PR-WY][0-9]{7}$)|(^[A-Z0-9]{5,15}$)/;

export const indiaPincodeRegex = /^\d{6}$/;
export const nonIndiaPincodeRegex = /^[A-Za-z0-9]{4,20}$/;

export enum IdType {
    Aadhaar = 'A',
    PAN = 'P',
    DrivingLicense = 'D',
    PassportNumber = 'I',
}

export const nomineeValidationSchema = Yup.object().shape({
    name: Yup.string()
        .required('Nominee name is required')
        .min(3, 'Invalid Nominee name. please enter valid name'),
    relation: Yup.string().test('relation', 'Relationship is required', (value) => {
        if (value === dashboard.UpdateNominee.selectRelation) {
            return false;
        }
        return true;
    }),
    gender: Yup.string().notRequired(),
    dob: Yup.date().nullable().notRequired(),
    email: Yup.string()
        .notRequired()
        .when([], {
            is: () => true,
            then: (schema) =>
                schema.test('email-format', 'Enter valid email address', (value) => {
                    if (!value || value.trim() === '') return true;
                    return emailRegex.test(value);
                }),
        }),
    mobile: Yup.string()
        .notRequired()
        .when([], {
            is: () => true,
            then: (schema) =>
                schema.test('mobile-format', 'Enter valid mobile number', (value) => {
                    if (!value || value.trim() === '') return true;
                    return mobileRegex.test(value);
                }),
        }),
    share: Yup.number()
        .required('Nominee Percentage required')
        .positive('Nominee Percentage should be greater than 0')
        .max(100, 'Nominee Percentage should not be greater than 100'),
    docName: Yup.string().notRequired(),
    docValue: Yup.string()
        .notRequired()
        .when('docName', {
            is: IdType.Aadhaar,
            then: (schema) =>
                schema
                    .test('aadhaar-format', 'Please enter last four digits of Aadhaar Number', (value) => {
                        if (!value || value.trim() === '') return true;
                        return /^\d{4}$/.test(value);
                    })
                    .test(
                        'is-valid-aadhaar',
                        'Enter a valid Aadhaar number',
                        (value) => {
                            if (!value || value.trim() === '') return true;
                            return value !== '0000';
                        },
                    ),
        })
        .when('docName', {
            is: IdType.PAN,
            then: (schema) =>
                schema.test('pan-format', 'Enter valid PAN', (value) => {
                    if (!value || value.trim() === '') return true;
                    return panRegex.test(value);
                }),
        })
        .when('docName', {
            is: IdType.DrivingLicense,
            then: (schema) =>
                schema.test('dl-format', 'Enter valid driving license', (value) => {
                    if (!value || value.trim() === '') return true;
                    return drivingLicenseRegex.test(value);
                }),
        })
        .when('docName', {
            is: IdType.PassportNumber,
            then: (schema) =>
                schema.test('passport-format', 'Enter valid Passport Number', (value) => {
                    if (!value || value.trim() === '') return true;
                    return passportNumberRegex.test(value);
                }),
        }),
    inCapacitationAmount: Yup.number().when('inCapacitation', {
        is: true,
        then: Yup.number().when('inCapacitationAmountorPercentageCheck', {
            is: 'Percentage',
            then: Yup.number()
                .min(1, 'Percentage must be at least 1%')
                .max(100, 'Percentage should not exceed 100%')
                .required('Percentage is required'),
            otherwise: Yup.number()
                .min(1, 'Amount must be at least 1₹')
                .required('Amount is required'),
        }),
    }),
    address1: Yup.string().notRequired(),
    city: Yup.string().notRequired(),
    state: Yup.string().notRequired(),
    country: Yup.string().notRequired(),
    pincode: Yup.string()
        .notRequired()
        .when('country', {
            is: 'India',
            then: (schema) =>
                schema
                    .test('india-pincode-min', 'Pincode should be at least 6 characters', (value) => {
                        if (!value || value.trim() === '') return true;
                        return value.length >= 6;
                    })
                    .test('india-pincode-max', 'Pincode should not be greater than 6 characters', (value) => {
                        if (!value || value.trim() === '') return true;
                        return value.length <= 6;
                    })
                    .test('india-pincode-format', 'Enter valid pincode', (value) => {
                        if (!value || value.trim() === '') return true;
                        return indiaPincodeRegex.test(value);
                    }),
            otherwise: (schema) =>
                schema
                    .test('non-india-pincode-min', 'Pincode should be at least 4 characters', (value) => {
                        if (!value || value.trim() === '') return true;
                        return value.length >= 4;
                    })
                    .test('non-india-pincode-max', 'Pincode should not be greater than 20 characters', (value) => {
                        if (!value || value.trim() === '') return true;
                        return value.length <= 20;
                    })
                    .test('non-india-pincode-format', 'Enter valid pincode', (value) => {
                        if (!value || value.trim() === '') return true;
                        return nonIndiaPincodeRegex.test(value);
                    }),
        }),
});
